import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered } from "lucide-react";
import { toEditorHtml } from "@/components/common/RichText";

/**
 * Lightweight rich-text editor for paragraph/body content.
 * Supports Bold, Italic, Underline, bullet & numbered lists, soft returns
 * (Shift+Enter → line break) and hard returns (Enter → new paragraph).
 * Deliberately NO font family/size controls. Emits sanitized-ish HTML via
 * onChange (final sanitization happens on render in <RichText/>).
 */
export const RichTextField = ({ value, onChange, placeholder = "Write…", testid }) => {
  const ref = useRef(null);
  const focusedRef = useRef(false);

  // Seed / sync the editable area from the external value, but never while the
  // user is typing (prevents caret jumps).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const desired = toEditorHtml(value);
    if (!focusedRef.current && el.innerHTML !== desired) {
      el.innerHTML = desired;
    }
  }, [value]);

  const emit = () => onChange(ref.current ? ref.current.innerHTML : "");

  const exec = (cmd) => {
    ref.current?.focus();
    try { document.execCommand(cmd, false, null); } catch (e) { console.warn(`RichTextField: execCommand('${cmd}') failed`, e); }
    emit();
  };

  const Btn = ({ cmd, label, Icon, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
      title={label}
      aria-label={label}
      data-testid={tid}
      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-1 py-1">
        <Btn cmd="bold" label="Bold" Icon={Bold} tid={testid ? `${testid}-bold` : undefined} />
        <Btn cmd="italic" label="Italic" Icon={Italic} tid={testid ? `${testid}-italic` : undefined} />
        <Btn cmd="underline" label="Underline" Icon={Underline} tid={testid ? `${testid}-underline` : undefined} />
        <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
        <Btn cmd="insertUnorderedList" label="Bullet list" Icon={List} tid={testid ? `${testid}-bullets` : undefined} />
        <Btn cmd="insertOrderedList" label="Numbered list" Icon={ListOrdered} tid={testid ? `${testid}-numbers` : undefined} />
        <span className="ml-2 hidden text-[10px] uppercase tracking-wide text-muted-foreground sm:inline">Enter = paragraph · Shift+Enter = line break</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emit}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; emit(); }}
        data-testid={testid}
        className="rte-editor rich-text min-h-[120px] w-full px-3 py-2 text-sm outline-none"
      />
    </div>
  );
};
